import DOMPurify, { Config } from "dompurify";

const questionTableStyleRules: Record<string, RegExp[]> = {
    width: [/^\d+(?:px|em|%)$/],
    border: [/^1px solid rgba\(0, 0, 0, 1\)$/],
};

const sanitize = (html: string | undefined, config?: Config): string =>
    DOMPurify.sanitize(html ?? "", config) as string;

const filterInlineStyles = (
    html: string,
    allowedStyles: Record<string, RegExp[]>,
): string => {
    const template = document.createElement("template");
    template.innerHTML = html;

    template.content.querySelectorAll<HTMLElement>("[style]").forEach((el) => {
        const allowedDeclarations: string[] = [];

        Array.from(el.style).forEach((propertyName) => {
            const value = el.style.getPropertyValue(propertyName).trim();
            const rules = allowedStyles[propertyName];

            if (rules?.some((rule) => rule.test(value))) {
                allowedDeclarations.push(`${propertyName}: ${value}`);
            }
        });

        if (allowedDeclarations.length) {
            el.setAttribute("style", allowedDeclarations.join("; "));
        } else {
            el.removeAttribute("style");
        }
    });

    return template.innerHTML;
};

export const sanitizeHtml = (html: string | undefined): string =>
    sanitize(html);

export const sanitizeQuestionTableHtml = (html: string | undefined): string =>
    filterInlineStyles(
        sanitize(html, {
            ADD_ATTR: ["style"],
        }),
        questionTableStyleRules,
    );

export const sanitizeTextCardHtml = (html: string | undefined): string => {
    const sanitizedHtml = sanitize(html, {
        ALLOWED_TAGS: [
            "span",
            "p",
            "b",
            "i",
            "u",
            "strong",
            "em",
            "pre",
            "ul",
            "ol",
            "li",
            "h1",
            "h2",
            "br",
            "a",
        ],
        ALLOWED_ATTR: ["class", "href", "target", "rel"],
    });

    const template = document.createElement("template");
    template.innerHTML = sanitizedHtml;

    template.content.querySelectorAll<HTMLAnchorElement>("a").forEach((anchor) => {
        if (!anchor.getAttribute("href")) {
            const span = document.createElement("span");
            span.innerHTML = anchor.innerHTML;
            anchor.replaceWith(span);
            return;
        }

        anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
    });

    return template.innerHTML;
};
